import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../foundation/altzero_base/base/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../foundation/altzero_base/base/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../../foundation/altzero_base/base/components/ui/table';
import { Badge } from '../../../foundation/altzero_base/base/components/ui/badge';
import { Zap, Leaf, DollarSign, Activity } from 'lucide-react';
import { formatEnergyRate, formatEnvRate } from '../lib/ratePrecision';
import { formatRateYearLabel } from '../lib/termLabel';

interface RateData {
    peakRates?: Record<string, number>;
    offpeakRates?: Record<string, number>;
    shoulderRates?: Record<string, number>;
    nonEnergyCharges?: Record<string, number | null>;
    environmentalCharges?: Record<string, any>;
}

interface DetailedOfferAnalysisProps {
    data: {
        smooth?: RateData;
        stepped?: RateData;
    };
    compact?: boolean;
}

const DetailedOfferAnalysis: React.FC<DetailedOfferAnalysisProps> = ({ data, compact = false }) => {
    if (!data) return null;

    const hasSmooth = !!data.smooth;
    const hasStepped = !!data.stepped;
    const defaultTab = hasSmooth ? 'smooth' : 'stepped';

    const renderRatesTable = (rates: RateData) => {
        const years = Object.keys(rates.peakRates || {}).sort();

        return (
            <div className={`space-y-${compact ? '4' : '6'}`}>
                {/* Energy Rates */}
                <div className="space-y-3">
                    <h4 className={`font-semibold text-gray-900 flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
                        <Zap className="w-4 h-4 text-orange-500" /> Energy Rates (c/kWh)
                    </h4>
                    <div className="rounded-md border border-gray-200 overflow-hidden">
                        <Table>
                            <TableHeader className="bg-gray-50">
                                <TableRow>
                                    <TableHead className={compact ? "w-[60px] text-[10px] h-8" : "w-[100px]"}>Year</TableHead>
                                    <TableHead className={compact ? "text-[10px] h-8" : ""}>Peak</TableHead>
                                    <TableHead className={compact ? "text-[10px] h-8" : ""}>Off-Peak</TableHead>
                                    <TableHead className={compact ? "text-[10px] h-8" : ""}>Shoulder</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {years.map((year) => (
                                    <TableRow key={year}>
                                        <TableCell className={`font-medium capitalize ${compact ? 'py-1 text-[10px]' : ''}`}>
                                            {formatRateYearLabel(year)}
                                        </TableCell>
                                        <TableCell className={compact ? 'py-1 text-[10px]' : ''}>
                                            {formatEnergyRate(rates.peakRates?.[year]) || '-'}
                                        </TableCell>
                                        <TableCell className={compact ? 'py-1 text-[10px]' : ''}>
                                            {formatEnergyRate(rates.offpeakRates?.[year]) || '-'}
                                        </TableCell>
                                        <TableCell className={compact ? 'py-1 text-[10px]' : ''}>
                                            {formatEnergyRate(rates.shoulderRates?.[year]) || '-'}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                {/* Environmental Charges */}
                {rates.environmentalCharges && (
                    <div className="space-y-3">
                        <h4 className={`font-semibold text-gray-900 flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
                            <Leaf className="w-4 h-4 text-green-500" /> Environmental Charges
                        </h4>
                        <div className={`grid gap-${compact ? '2' : '4'}`}>
                            {Object.entries(rates.environmentalCharges).map(([type, yearsData]: [string, any]) => (
                                <Card key={type} className="border-green-100 shadow-sm">
                                    <CardHeader className={`bg-green-50/50 border-b border-green-100 ${compact ? 'p-2' : 'p-3'}`}>
                                        <CardTitle className={`font-bold text-green-800 uppercase tracking-wider ${compact ? 'text-[10px]' : 'text-xs'}`}>
                                            {type}
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="p-0">
                                        <Table>
                                            <TableHeader>
                                                <TableRow className="hover:bg-transparent">
                                                    <TableHead className={`text-xs ${compact ? 'h-6 text-[10px]' : 'h-8'}`}>Year</TableHead>
                                                    <TableHead className={`text-xs text-right ${compact ? 'h-6 text-[10px]' : 'h-8'}`}>Rate</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {Object.entries(yearsData).map(([year, details]: [string, any]) => (
                                                    <TableRow key={year} className="hover:bg-green-50/30">
                                                        <TableCell className={`capitalize ${compact ? 'py-1 text-[10px]' : 'py-2 text-xs'}`}>
                                                            {formatRateYearLabel(year)}
                                                        </TableCell>
                                                        <TableCell className={`text-right font-medium ${compact ? 'py-1 text-[10px]' : 'py-2 text-xs'}`}>
                                                            {formatEnvRate(details.ratePerKwh)}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                )}

                {/* Non-Energy Charges */}
                {rates.nonEnergyCharges && (
                    <div className="space-y-3">
                        <h4 className={`font-semibold text-gray-900 flex items-center gap-2 ${compact ? 'text-xs' : 'text-sm'}`}>
                            <DollarSign className="w-4 h-4 text-blue-500" /> Non-Energy Charges
                        </h4>
                        <div className={`grid ${compact ? 'grid-cols-1 gap-2' : 'grid-cols-2 gap-3'}`}>
                            {Object.entries(rates.nonEnergyCharges).map(([name, value]) => (
                                <div key={name} className={`flex items-center justify-between bg-gray-50 rounded-lg border border-gray-100 ${compact ? 'p-2' : 'p-3'}`}>
                                    <span className={`text-gray-600 ${compact ? 'text-[10px]' : 'text-xs'}`}>{name}</span>
                                    <span className={`font-semibold text-gray-900 ${compact ? 'text-xs' : 'text-sm'}`}>
                                        {value ? `$${value}` : 'N/A'}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (compact) {
        return (
            <div className="bg-white rounded-lg">
                <Tabs defaultValue={defaultTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-4 h-8">
                        <TabsTrigger value="smooth" disabled={!hasSmooth} className="text-xs h-7">Smooth</TabsTrigger>
                        <TabsTrigger value="stepped" disabled={!hasStepped} className="text-xs h-7">Stepped</TabsTrigger>
                    </TabsList>

                    {hasSmooth && (
                        <TabsContent value="smooth" className="mt-0">
                            {renderRatesTable(data.smooth!)}
                        </TabsContent>
                    )}

                    {hasStepped && (
                        <TabsContent value="stepped" className="mt-0">
                            {renderRatesTable(data.stepped!)}
                        </TabsContent>
                    )}
                </Tabs>
            </div>
        );
    }

    return (
        <Card className="border-gray-200 shadow-sm mt-6">
            <CardHeader className="pb-2 border-b border-gray-100">
                <div className="flex items-center justify-between">
                    <CardTitle className="text-lg font-bold flex items-center gap-2">
                        <Activity className="w-5 h-5 text-indigo-500" />
                        Detailed Rate Analysis
                    </CardTitle>
                    <div className="flex gap-2">
                        {hasSmooth && <Badge variant="outline" className="bg-indigo-50 text-indigo-700 border-indigo-200">Smooth</Badge>}
                        {hasStepped && <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">Stepped</Badge>}
                    </div>
                </div>
            </CardHeader>
            <CardContent className="p-6">
                <Tabs defaultValue={defaultTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2 mb-6">
                        <TabsTrigger value="smooth" disabled={!hasSmooth}>Smooth Rates</TabsTrigger>
                        <TabsTrigger value="stepped" disabled={!hasStepped}>Stepped Rates</TabsTrigger>
                    </TabsList>

                    {hasSmooth && (
                        <TabsContent value="smooth" className="mt-0">
                            {renderRatesTable(data.smooth!)}
                        </TabsContent>
                    )}

                    {hasStepped && (
                        <TabsContent value="stepped" className="mt-0">
                            {renderRatesTable(data.stepped!)}
                        </TabsContent>
                    )}
                </Tabs>
            </CardContent>
        </Card>
    );
};

export default DetailedOfferAnalysis;
